<template>
  <div
    class="ui-textarea"
    :class="[$attrs.class, `ui-textarea--${size}`, { 'ui-textarea--error': hasError, 'ui-textarea--disabled': disabled, 'ui-textarea--floating': floatingError }]"
    :style="rootStyle"
  >
    <label v-if="label" :for="resolvedId">
      {{ label }}
      <span v-if="required" aria-hidden="true">*</span>
      <slot name="labelTooltip" />
    </label>
    <textarea
      :id="resolvedId"
      ref="element"
      v-bind="nativeAttrs"
      :aria-describedby="message ? messageId : undefined"
      :aria-errormessage="hasError && message ? messageId : undefined"
      :aria-invalid="hasError || undefined"
      :disabled="disabled"
      :name="name"
      :placeholder="placeholder"
      :required="required"
      :value="modelValue"
      @input="update"
    />
    <p v-if="message" :id="messageId">{{ message }}</p>
  </div>
</template>
<script setup lang="ts">
  import { computed, ref, useAttrs, useId } from 'vue'

  defineOptions({ inheritAttrs: false, name: 'UiTextarea' })
  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      error?: boolean | string
      floatingError?: boolean
      helperText?: string
      id?: string
      label?: string
      modelValue?: string
      name?: string
      placeholder?: string
      required?: boolean
      size?: 'md' | 'sm'
    }>(),
    { modelValue: '', size: 'md' },
  )
  const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
  const attrs = useAttrs()
  const generatedId = useId()
  const element = ref<HTMLTextAreaElement>()
  const resolvedId = computed(() => props.id ?? `ui-textarea-${generatedId}`)
  const messageId = computed(() => `${resolvedId.value}-message`)
  const hasError = computed(
    () => props.error === true || (typeof props.error === 'string' && props.error.length > 0),
  )
  const message = computed(() =>
    typeof props.error === 'string' && props.error ? props.error : props.helperText,
  )
  const nativeAttrs = computed(() =>
    Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style')),
  )
  const rootStyle = computed(() => attrs.style)
  const update = (event: Event) => {
    emit('update:modelValue', (event.target as HTMLTextAreaElement).value)
  }
  const focus = () => element.value?.focus()
  defineExpose({ element, focus })
</script>
<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

/* Disabled, error and focus states intentionally override hover. */
/* stylelint-disable no-descending-specificity */

.ui-textarea {
 display: flex;
 flex-direction: column;
 gap: 6px;
 position: relative;
 width: 100%;

}

label {
 @include typography.font-style('text-sm', 'medium');

 color: var(--color-text-text-secondary-700);

}

label span {
 color: var(--color-brand-600);

}

textarea {
 @include typography.font-style('text-md', 'regular');

 background: var(--color-background-bg-primary);
 border: 1px solid var(--color-border-border-primary);
 border-radius: var(--radius-md);
 box-shadow: 0 1px 2px var(--color-effects-shadows-shadow-xs);
 color: var(--color-text-text-primary-900);
 min-height: 100px;
 outline: 0;
 padding: 10px 12px;
 resize: vertical;
 transition: border-color 0.2s ease, box-shadow 0.2s ease;

}

.ui-textarea--sm textarea {
 @include typography.font-style('text-sm', 'regular');

}

textarea:hover:not(:disabled) {
 border-color: var(--color-gray-light-400);

}

textarea:focus-visible {
 border-color: var(--color-border-border-brand);
 outline: 1px solid var(--color-border-border-brand);

}

.ui-textarea--error textarea {
 border-color: var(--color-border-border-error-subtle);

}

.ui-textarea--error textarea:focus-visible {
 border-color: var(--color-border-border-error-subtle);
 outline: 1px solid var(--color-border-border-error-subtle);

}

textarea:disabled {
 background: var(--color-gray-neutral-50);
 color: var(--color-text-text-disabled);
 cursor: not-allowed;
 opacity: 0.5;

}

p {
 @include typography.font-style('text-sm', 'regular');

 color: var(--color-text-text-tertiary-600);
 margin: 0;

}

.ui-textarea--error p {
 color: var(--color-error-600);

}

.ui-textarea--floating p {
 background: var(--color-background-bg-primary);
 border-radius: var(--radius-xs);
 box-shadow: 0 2px 4px var(--color-effects-shadows-shadow-xs);
 left: 0;
 padding: 4px 8px;
 position: absolute;
 top: calc(100% + 4px);
 z-index: 5;

}

</style>
